import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/cms';

export default function BlivVikar() {
  const faggrupper = [
    {
      title: 'Social- og sundhedsassistenter (SSA) & SSH',
      icon: 'fa-user-nurse',
      desc: 'Til opgaver med sundhedsfaglig pleje, medicinhåndtering, observation samt omsorg for borgere med somatiske og psykiatriske udfordringer.',
      badge: 'Sundhedsfaglig',
    },
    {
      title: 'Pædagoger & Socialpædagoger',
      icon: 'fa-hands-holding-child',
      desc: 'Til relationsarbejde, strukturering af hverdagen, pædagogisk støtte, low arousal og konfliktnedtrappende tilgange på bosteder og institutioner.',
      badge: 'Pædagogisk',
    },
    {
      title: 'Pædagogiske assistenter (PAU)',
      icon: 'fa-graduation-cap',
      desc: 'Til praktisk pædagogisk støtte, aktiviteter, samvær og tryghedsskabende rammer for børn, unge og voksne med særlige behov.',
      badge: 'Assistent',
    },
    {
      title: 'Sygeplejersker & Ergoterapeuter',
      icon: 'fa-stethoscope',
      desc: 'Til specialiserede opgaver med komplekse forløb, sanseintegration, genoptræning og tværfaglig koordinering i akutte og længerevarende indsatser.',
      badge: 'Specialiseret',
    },
    {
      title: 'Personer med min. 3 års relevant erfaring',
      icon: 'fa-briefcase',
      desc: 'Har du minimum tre års dokumenteret erhvervserfaring fra det socialpædagogiske, psykiatriske eller specialiserede område, er du også meget velkommen hos os.',
      badge: 'Erfaring',
    },
  ];

  const benefits = [
    {
      icon: 'fa-calendar-check',
      title: 'Maksimal fleksibilitet',
      desc: 'Du bestemmer selv, hvor meget du vil arbejde, og hvilke vagttyper (dag, aften, nat, døgn eller weekend) der passer ind i dit liv.',
    },
    {
      icon: 'fa-headset',
      title: '24/7 døgnstøtte og bagvagt',
      desc: 'Du er aldrig overladt til dig selv. Vores vagttelefon er åben døgnet rundt for sparring, rådgivning og akut opbakning.',
    },
    {
      icon: 'fa-scale-balanced',
      title: 'Ordnede forhold og god løn',
      desc: 'Vi tilbyder konkurrencedygtig løn, tillæg efter overenskomstmæssige principper samt gennemskuelige og trygge arbejdsvilkår.',
    },
    {
      icon: 'fa-chalkboard-user',
      title: 'Faglig sparring og kurser',
      desc: 'Vi prioriterer løbende opkvalificering, herunder kurser i bl.a. konflikthåndtering, low arousal og skånsom magtanvendelse.',
    },
    {
      icon: 'fa-users',
      title: 'Meningsfulde opgaver',
      desc: 'Gør en reel forskel for mennesker på bosteder, døgninstitutioner, enkeltsager og aflastningsordninger i hele Jylland og på Fyn.',
    },
    {
      icon: 'fa-handshake-simple',
      title: 'Respekt og tæt dialog',
      desc: 'Hos Akutvikaren er du ikke bare et nummer. Vi kender vores vikarer, lytter til dine ønsker og matcher dig til de rette opgaver.',
    },
  ];

  const expectations = [
    'Uddannelse inden for det social- og sundhedsfaglige/pædagogiske område ELLER minimum 3 års relevant erhvervserfaring',
    'Menneskeligt overskud, empati og en rolig, tydelig tilgang i pressede situationer',
    'Kendskab til eller interesse for low arousal, konfliktnedtrapning og relationsarbejde',
    'Høj ansvarsfølelse, pålidelighed og gode samarbejdsevner',
    'Gyldig straffeattest og børneattest (indhentes ved ansættelse)',
    'Kørekort B er ofte en fordel, men ikke altid et krav',
  ];

  return (
    <>
      <SEO
        title="Bliv vikar - Akut Vikaren ApS | Social- og sundhedsfaglige vikarer"
        description="Bliv vikar hos Akutvikaren. Vi ansætter social og sundheds faglige uddannet medarbejder eller personer med minimum tre års relevant erhvervs erfaring. Fleksible vagter i Jylland og på Fyn."
        ogImage="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-medarbejder-portraet.webp"
      />

      {/* 1. HERO SECTION */}
      <section className="relative bg-[#041c2c] text-white py-16 sm:py-24 border-b border-slate-800 overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-medarbejder-i-moede.webp"
            alt="Bliv vikar hos Akut Vikaren"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 35%' }}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#041c2c]/95 via-[#041c2c]/85 to-[#041c2c]/65" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/80 border border-slate-700/80 text-xs font-semibold text-[#b39366] uppercase tracking-wider">
              <i className="fa-solid fa-user-plus text-xs" aria-hidden="true"></i>
              <span>Karriere & Vikaransættelse</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Bliv vikar hos Akutvikaren
            </h1>

            <p className="text-slate-200 text-lg sm:text-xl font-normal leading-relaxed">
              Vil du være med til at gøre en mærkbar forskel for mennesker med særlige behov? Hos Akutvikaren tilbyder vi et meningsfuldt vikarjob med stor frihed, stærk faglighed og et trygt bagland.
            </p>

            {/* Exact Customer Text Callout */}
            <div className="p-4 sm:p-5 rounded-xl bg-slate-900/90 border-l-4 border-[#b39366] shadow-lg">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-circle-check text-[#b39366] text-xl shrink-0 mt-0.5" aria-hidden="true"></i>
                <div className="space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#b39366]">Hvem ansætter vi?</span>
                  <p className="text-white text-base sm:text-lg font-semibold leading-snug">
                    Vi ansætter social og sundheds faglige uddannet medarbejder eller personer med minimum tre års relevant erhvervs erfaring ..
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="#ansoeg-formular"
                className="inline-flex items-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white font-semibold px-6 py-3.5 rounded shadow-sm hover:shadow transition-all text-sm"
              >
                <span>Send ansøgning online</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true"></i>
              </a>

              <a
                href="tel:+4523964042"
                className="inline-flex items-center gap-2 bg-slate-800/90 hover:bg-slate-700 text-white font-semibold px-6 py-3.5 rounded border border-slate-700 transition-all text-sm"
              >
                <i className="fa-solid fa-phone text-[#b39366] text-xs" aria-hidden="true"></i>
                <span>Ring (+45) 23 96 40 42</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2. HVILKE FAGGRUPPER TAGER VI IMOD? */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#faf9f6] border-b border-[#eae6dd]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Faglige profiler</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#041c2c] tracking-tight">
              Hvilke faggrupper tager vi imod?
            </h2>
            <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
              Vi løser specialiserede opgaver inden for socialpsykiatri, bostøtte, aflastning og akut anbringelse. Derfor vægter vi faglighed, ro og relationskompetence højt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {faggrupper.map((fg, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl border border-[#eae6dd] p-6 sm:p-7 shadow-xs hover:shadow-md hover:border-[#b39366] transition-all flex flex-col justify-between"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-xl bg-[#b39366]/15 text-[#b39366] flex items-center justify-center text-xl">
                      <i className={`fa-solid ${fg.icon}`} aria-hidden="true"></i>
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-[#041c2c] text-white">
                      {fg.badge}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-lg font-bold text-[#041c2c] tracking-tight">
                      {fg.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {fg.desc}
                    </p>
                  </div>
                </div>

                <div className="pt-4 mt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-semibold text-[#b39366]">
                  <i className="fa-solid fa-check text-[10px]" aria-hidden="true"></i>
                  <span>Relevant for Akutvikaren</span>
                </div>
              </div>
            ))}

            {/* Direct inquiry box */}
            <div className="bg-[#041c2c] text-white rounded-xl border border-slate-800 p-6 sm:p-7 shadow-sm flex flex-col justify-between space-y-4">
              <div className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Er du i tvivl?</span>
                <h3 className="text-lg font-bold text-white tracking-tight">
                  Passer din baggrund?
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Er du i tvivl om, hvorvidt din profil eller dine erfaringer matcher vores opgaver, er du altid velkommen til at ringe eller sende dit CV til os for en uforpligtende snak.
                </p>
              </div>

              <a
                href="tel:+4523964042"
                className="inline-flex items-center justify-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white font-semibold py-3 px-4 rounded text-sm transition-all"
              >
                <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                <span>Ring og tag en snak</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FORDELE VED AT VÆRE VIKAR HOS OS */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white border-b border-[#eae6dd]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-7 space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Trygt vikararbejde</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#041c2c] tracking-tight">
                Hvorfor blive vikar hos Akutvikaren?
              </h2>
              <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
                Vi ved, at vores vikarer er vores vigtigste ressource. Derfor gør vi os umage med at skabe trygge, fleksible og ordentlige rammer om dit arbejde. Vi kender det socialpædagogiske og psykiatriske felt indgående, og vi forstår hverdagens udfordringer.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-slate-200">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/akut-vikaren-medarbejder-portraet.webp', 800, 85)}
                  alt="Medarbejder hos Akutvikaren"
                  className="w-full h-80 sm:h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#041c2c]/90 via-[#041c2c]/30 to-transparent flex items-end p-6">
                  <div className="text-white space-y-1">
                    <p className="font-bold text-lg">En del af et fagligt stærkt fællesskab</p>
                    <p className="text-xs text-slate-300">Vi dækker hele Jylland og Fyn med fokus på relationer og tryghed.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((b, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#b39366]/20 text-[#b39366] flex items-center justify-center text-lg shrink-0 mt-1">
                  <i className={`fa-solid ${b.icon}`} aria-hidden="true"></i>
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-lg font-bold text-[#041c2c]">{b.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. HVAD FORVENTER VI? */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#faf9f6] border-b border-[#eae6dd]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Krav og forventninger</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#041c2c] tracking-tight">
              Hvad forventer vi af dig som vikar?
            </h2>
            <p className="text-slate-700 text-base leading-relaxed">
              Arbejdet med mennesker med komplekse problemstillinger stiller krav til din faglige og personlige integritet. Vi søger kolleger, som deler vores værdier om nærvær, empati og ro.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-[#eae6dd] p-6 sm:p-10 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {expectations.map((exp, idx) => (
                <div key={idx} className="flex items-start gap-3.5">
                  <div className="w-6 h-6 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center text-xs shrink-0 mt-0.5">
                    <i className="fa-solid fa-check" aria-hidden="true"></i>
                  </div>
                  <p className="text-slate-800 text-sm sm:text-base leading-relaxed font-medium">
                    {exp}
                  </p>
                </div>
              ))}
            </div>

            {/* Note box */}
            <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#f8f7f4] -mx-6 -mb-6 sm:-mx-10 sm:-mb-10 p-6 sm:p-8 rounded-b-2xl">
              <div className="space-y-1 text-center sm:text-left">
                <h4 className="text-base font-bold text-[#041c2c]">Klar til at søge?</h4>
                <p className="text-slate-600 text-xs sm:text-sm">
                  Udfyld formularen nedenfor eller kontakt os direkte på telefon eller mail.
                </p>
              </div>

              <a
                href="#ansoeg-formular"
                className="inline-flex items-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white text-sm font-semibold px-5 py-2.5 rounded shadow-xs whitespace-nowrap"
              >
                <span>Gå til ansøgningsformular</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 5. ANSØGNINGSSEKTION MED FORMULAR */}
      <section id="ansoeg-formular" className="py-16 sm:py-20 lg:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-3 mb-12 sm:mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Ansøg i dag</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#041c2c] tracking-tight">
              Søg som vikar hos Akutvikaren
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Send os dine oplysninger, så kontakter vi dig hurtigst muligt for en uforpligtende samtale om mulighederne for et samarbejde.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Contact Card */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-[#f8f7f4] border border-[#eae6dd] rounded-2xl p-6 sm:p-8 space-y-6">
                <div className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#b39366]">Direkte kontakt</span>
                  <h3 className="text-xl font-bold text-[#041c2c]">Har du spørgsmål til stillingen?</h3>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    Du er altid velkommen til at gribe knoglen eller sende os en mail, hvis du vil høre mere om arbejdsforholdene, vagttyperne eller timeantallet.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Telefon */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-phone text-base" aria-hidden="true"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Telefon</p>
                      <a
                        href="tel:+4523964042"
                        className="text-base font-bold text-[#041c2c] hover:text-[#b39366] transition-colors whitespace-nowrap block"
                      >
                        (+45) 23 96 40 42
                      </a>
                      <p className="text-xs text-slate-500 mt-0.5">Vi besvarer opkald døgnet rundt.</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-envelope text-base" aria-hidden="true"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">E-mail</p>
                      <a
                        href="mailto:kontakt@akutvikaren.dk"
                        className="text-base font-semibold text-[#041c2c] hover:text-[#b39366] transition-colors whitespace-nowrap block"
                      >
                        kontakt@akutvikaren.dk
                      </a>
                      <p className="text-xs text-slate-500 mt-0.5">Send gerne dit CV direkte til os.</p>
                    </div>
                  </div>

                  {/* Adresse */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-location-dot text-base" aria-hidden="true"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Adresse</p>
                      <p className="text-sm font-medium text-[#041c2c]">Alphavej 17, 8270 Højbjerg</p>
                      <p className="text-xs text-slate-500 mt-0.5">Dækker hele Jylland og Fyn</p>
                    </div>
                  </div>
                </div>

                {/* Reassurance banner */}
                <div className="p-4 rounded-xl bg-white border border-[#eae6dd] space-y-1.5">
                  <div className="flex items-center gap-2 text-[#b39366] text-xs font-bold uppercase tracking-wider">
                    <i className="fa-solid fa-lock" aria-hidden="true"></i>
                    <span>Fortrolig behandling</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Alle henvendelser og ansøgninger behandles med fuld diskretion og fortrolighed.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Form Column */}
            <div className="lg:col-span-7">
              <div className="bg-[#f8f7f4] border border-[#eae6dd] rounded-2xl p-0">
                <ContactForm
                  title="Ansøgningsformular for vikarer"
                  subtitle="Udfyld formularen, så kontakter vi dig hurtigst muligt for en uforpligtende samtale."
                  isVikarForm={true}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
